/**
 * Frontend mirror of the AI copilot chat contract (`app/schemas/ai.py`).
 *
 * C2 — ChatResponse.attachments / suggested_prompts.
 * C3 — ChatContext (page, invoice_id, queue, tab, highlight, selection).
 * Keep this file in lock-step with the backend schema; other components import from here.
 */

export type ChatRole = 'user' | 'assistant' | 'system'

export interface ChatHistoryMessage {
  role: ChatRole
  content: string
}

/** C3 — what the frontend tells the copilot about where the user is. */
export interface ChatContext {
  /** Route pathname, e.g. "/workbench". */
  page: string
  /** Invoice DB id (from `?invoice=` on /workbench), never the human invoice_id string. */
  invoice_id: number | null
  /** Workbench `?queue=` (explorer tab or `status:<status>`). */
  queue: string | null
  /** Active workbench tab label / explorer `?status=` tab. */
  tab: string | null
  /** Policy rule_code or id from `?highlight=` on /ai/policies. */
  highlight: string | null
  /** Anything else the page wants the copilot to know (selected explorer rows, …). */
  selection: Record<string, unknown> | null
}

export type ChatAttachmentType = 'file' | 'invoice' | 'invoices' | 'action' | 'link' | 'chart' | 'policy'

/** C2 — one structured card rendered under an assistant message. */
export interface ChatAttachment {
  type: ChatAttachmentType
  title: string
  description?: string | null
  /** file: download_path (`/api/ap/reports/download/<file>.pdf`); link: an app route. */
  url?: string | null
  data?: Record<string, unknown> | null
}

export interface ToolCallResult {
  id: string
  name: string
  args: Record<string, unknown>
  result?: unknown
}

export interface ChatResponse {
  response: string
  tool_calls?: ToolCallResult[] | null
  suggestions?: string[] | null
  confidence?: number | null
  attachments?: ChatAttachment[] | null
  suggested_prompts?: string[] | null
}

export interface ChatRequest {
  message: string
  history: ChatHistoryMessage[]
  context: ChatContext
}

// ---------------------------------------------------------------------------
// Typed views over `attachment.data` (all fields optional — backend may omit)
// ---------------------------------------------------------------------------

export interface FileAttachmentData {
  filename?: string
  size_bytes?: number
  report_type?: string
  created_at?: string
  summary?: string
}

export interface InvoiceAttachmentData {
  id?: number
  invoice_db_id?: number
  invoice_id?: string
  vendor_name?: string | null
  status?: string
  review_category?: string | null
  grand_total?: number | null
  subtotal?: number | null
  tax?: number | null
  currency?: string | null
  invoice_date?: string | null
  due_date?: string | null
  related_po_numbers?: string[]
  related_grn_numbers?: string[]
  match_confidence?: number | null
  review_focus?: string | null
  queue?: string | null
}

export interface InvoicesAttachmentData {
  rows?: InvoiceAttachmentData[]
  total?: number
  queue?: string | null
}

export interface ActionAttachmentData {
  invoice_id?: number
  invoice_id_str?: string
  from_status?: string | null
  to_status?: string | null
  audit_id?: number | string | null
  status?: 'done' | 'blocked' | string
  reason?: string | null
  results?: Array<{
    invoice_id?: number
    invoice_id_str?: string
    status?: 'done' | 'blocked' | string
    from_status?: string | null
    to_status?: string | null
    reason?: string | null
  }>
}

export interface PolicyAttachmentData {
  rule_code?: string
  name?: string
  id?: string
}

/** Name of the window event fired after the copilot mutates an invoice. */
export const INVOICE_UPDATED_EVENT = 'ap:invoice-updated'

export interface InvoiceUpdatedDetail {
  invoiceId: number | null
}
